import React from 'react';
import {
  Container, Box, Typography, Card, CardContent, Grid, List, ListItem,
  ListItemIcon, ListItemText, Accordion, AccordionSummary, AccordionDetails,
  Button, Chip
} from '@mui/material';
import {
  ExpandMore, CheckCircle, TrendingUp, Star, Speed, Assignment,
  Lightbulb, Phone, Email, VideoLibrary
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Verifier Help Page
 * Provides tips and guidance for improving completion rate and performance
 */
const VerifierHelpPage = () => {
  const navigate = useNavigate();

  const tips = [
    {
      title: 'Accept Assignments Carefully',
      icon: <Assignment color="primary" />,
      description: 'Only accept assignments you can realistically complete. Review the requirements, timeline, and location before accepting.',
      actionItems: [
        'Check your schedule before accepting',
        'Verify you have the required skills/certifications',
        'Ensure the location is within your service area',
        'Read all assignment details thoroughly'
      ]
    },
    {
      title: 'Communicate Proactively',
      icon: <Phone color="primary" />,
      description: 'Keep clients informed throughout the verification process. Good communication prevents misunderstandings and cancellations.',
      actionItems: [
        'Confirm assignments promptly',
        'Update clients on your progress',
        'Ask questions if requirements are unclear',
        'Notify immediately if delays occur'
      ]
    },
    {
      title: 'Complete Assignments On Time',
      icon: <Speed color="primary" />,
      description: 'Meeting deadlines is crucial for maintaining a high completion rate and professional reputation.',
      actionItems: [
        'Plan your route and schedule in advance',
        'Start assignments early when possible',
        'Set reminders for deadlines',
        'Submit reports promptly after completion'
      ]
    },
    {
      title: 'Provide Quality Work',
      icon: <Star color="primary" />,
      description: 'High-quality verifications lead to satisfied clients, good reviews, and more assignments.',
      actionItems: [
        'Take clear, well-lit photos',
        'Write detailed, accurate reports',
        'Follow verification checklists completely',
        'Double-check your work before submission'
      ]
    },
    {
      title: 'Build Your Profile',
      icon: <TrendingUp color="primary" />,
      description: 'A complete, professional profile helps you get better assignments and build trust with clients.',
      actionItems: [
        'Upload professional certifications',
        'Add relevant experience and specializations',
        'Keep your availability status updated',
        'Maintain active insurance coverage'
      ]
    }
  ];

  const faqs = [
    {
      question: 'How is completion rate calculated?',
      answer: 'Your completion rate is the percentage of accepted assignments you successfully complete. It only counts assignments that were accepted and either completed or cancelled. Higher completion rates (85%+) demonstrate reliability and professionalism.'
    },
    {
      question: 'Why is my completion rate low?',
      answer: 'Low completion rates can result from accepting assignments you cannot complete, missing deadlines, or having assignments cancelled. Review your recent assignments to identify patterns and areas for improvement.'
    },
    {
      question: 'How can I improve my completion rate?',
      answer: 'Focus on accepting only assignments you can definitely complete, communicate clearly with clients, meet all deadlines, and provide high-quality work. Be selective about which assignments you accept rather than accepting everything.'
    },
    {
      question: 'Will a low completion rate affect my account?',
      answer: 'While you can continue working with any completion rate, maintaining 85%+ is considered professional status and may give you priority for high-value assignments. Very low rates may result in reduced visibility in the marketplace.'
    },
    {
      question: 'Can I recover from a low completion rate?',
      answer: 'Yes! Your completion rate will improve as you successfully complete more assignments. Focus on quality over quantity, and your rate will gradually increase over time.'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="600" gutterBottom>
          Verifier Help & Tips
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Learn how to maintain a high completion rate and succeed as a professional verifier
        </Typography>
      </Box>

      {/* Completion Rate Info */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CheckCircle sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h5" fontWeight="600">
                Target: 85%+ Completion Rate
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Professional verifiers maintain high completion rates
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2">
            Your completion rate shows clients how reliable you are. A high completion rate (85% or above) 
            demonstrates professionalism, builds trust, and can lead to more high-value assignments.
          </Typography>
        </CardContent>
      </Card>

      {/* Tips Grid */}
      <Typography variant="h5" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
        Tips for Success
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {tips.map((tip, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {tip.icon}
                  <Typography variant="h6" fontWeight="600">
                    {tip.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {tip.description}
                </Typography>
                <List dense>
                  {tip.actionItems.map((item, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* FAQs */}
      <Typography variant="h5" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
        Frequently Asked Questions
      </Typography>
      <Box sx={{ mb: 4 }}>
        {faqs.map((faq, index) => (
          <Accordion key={index}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="600">{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Quick Actions */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Need More Help?
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Assignment />}
                onClick={() => navigate('/verifier/marketplace')}
              >
                Browse Available Assignments
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Lightbulb />}
                onClick={() => navigate('/verifier/profile')}
              >
                Update My Profile
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<VideoLibrary />}
                onClick={() => window.open('https://eqaya.com/verifier-training', '_blank')}
              >
                Watch Training Videos
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Email />}
                onClick={() => window.location.href = 'mailto:info@eqaya.com'}
              >
                Contact Support
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default VerifierHelpPage;
